/*
 * Signature Category detail (iter266)
 * -----------------------------------
 * The interior of one shelf. Header with the category name + blurb,
 * a Featured installation (top precomputed / hero one), and the
 * full list of installations in the category. Each card tap opens
 * the DetailSheet — same one used on the earlier page — with PLAY
 * NOW / TWEAK affordances.
 *
 * If the URL includes ?bp=<blueprint_id>, that installation's
 * DetailSheet is opened automatically on mount (search deep-link).
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import SoulumoHeader from "../../components/SoulumoHeader";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Loader2, Clock, Play, Wand2, Check, X, Mic, Plus, Heart } from "lucide-react";
import { displayName } from "./signatureWorlds";
import { NAVY_BG } from "./SignatureBits";
import { GolfPackButton } from "../../components/soulumoCreate/GolfPackButton";
import { SheetPlayer } from "../../components/soulumoSignature/SheetPlayer";
import { FavHeart } from "../../components/soulumoSignature/FavHeart";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const TOK = {
  ink: "#0d1f3a",
  gold: "#d7bb7a",
  goldSoft: "#c8a45a",
  goldMist: "rgba(215,187,122,0.36)",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
};

export default function SignatureCategoryPage() {
  const nav = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBp, setOpenBp] = useState(null);
  const { cloning, cloneError, setCloneError, onPlayNow, added } = useBlueprintPlay();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/audio-journeys/blueprints`, auth());
        if (cancelled) return;
        // Category filter + optional keyword filter (used by the
        // taxonomy sub-heading rows — e.g. "Golf" is
        // /sport_movement?keyword=golf). Matches against id / label /
        // theme / custom_concern / blurb so the same predicate works
        // for both structured themes and simple label matches.
        const kw = (searchParams.get("keyword") || searchParams.get("theme") || "").toLowerCase();
        const its = (r.data.items || []).filter((i) => {
          if (i.category !== categoryId) return false;
          if (!kw) return true;
          const hay = [i.id, i.label, i.theme, i.custom_concern, i.blurb]
            .filter(Boolean).join(" ").toLowerCase();
          return hay.includes(kw);
        });
        setItems(its);
        setCategories(r.data.categories || []);
        // Deep-link ?bp=... opens the sheet.
        const dl = searchParams.get("bp");
        if (dl) {
          const found = its.find((i) => i.id === dl);
          if (found) setOpenBp(found);
        }
      } catch (e) {
        if (cancelled) return;
        setError("Couldn't load this category.");
        console.error("[signature-category]", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [categoryId, searchParams]);

  const cat = useMemo(() => categories.find((c) => c.id === categoryId), [categories, categoryId]);
  const aura = cat?.aura || TOK.gold;

  const featured = items.find((i) => i.precomputed_ready) || items[0];
  const rest = items.filter((i) => i.id !== featured?.id).sort((a, b) => (b.favourite ? 1 : 0) - (a.favourite ? 1 : 0));
  const setFav = (id, on) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, favourite: on } : x)));
    setOpenBp((b) => (b && b.id === id ? { ...b, favourite: on } : b));
  };

  const onTweak = (bp) => nav(`/my-journeys?blueprint=${bp.id}`);

  return (
    <div
      className="min-h-screen"
      style={{ background: NAVY_BG, backgroundAttachment: "fixed", color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
      data-testid="signature-category-page"
    >
      <SoulumoHeader />
      <header
        className="sticky top-14 z-10 backdrop-blur-md"
        style={{ background: "rgba(13,31,58,0.86)", borderBottom: `1px solid ${aura}55` }}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-5 py-4">
          <button
            onClick={() => nav("/signature-installations")}
            data-testid="signature-category-back"
            aria-label="Back"
            className="inline-flex items-center justify-center rounded-full transition-all"
            style={{ width: 44, height: 44, background: "rgba(245,236,217,0.04)", border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex-1">
            <div className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ color: aura }}>
              {items.length} {items.length === 1 ? "installation" : "installations"}
            </div>
            <h1
              className="text-2xl font-light leading-tight"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}
            >
              {cat?.label || "Category"}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-24 pt-6">
        {cat?.blurb && (
          <p
            className="text-[0.9rem] italic leading-relaxed mb-8"
            style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}
          >
            {cat.blurb}
          </p>
        )}

        {!loading && !error && items.some((i) => i.id.includes("golf")) && (
          <>
            <p className="text-[0.95rem] italic leading-relaxed mb-5" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }} data-testid="golf-library-note">
              A full library of signature golf installations, carefully curated and ready to play. Each one goes deeper than long game and short game: the walk between shots, the closing holes, the comeback.
            </p>
            <GolfPackButton className="mb-8" />
          </>
        )}

        {loading && (
          <div className="flex items-center gap-3 py-16 justify-center" style={{ color: TOK.creamDim }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="py-16 text-center italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }} data-testid="signature-category-empty">
            New installations for this category are coming soon.
          </div>
        )}

        {!loading && !error && featured && (
          <>
            {/* Featured (hero) */}
            <button
              onClick={() => setOpenBp(featured)}
              data-testid={`signature-featured-${featured.id}`}
              className="text-left w-full rounded-3xl p-6 mb-6 transition-all"
              style={{
                background: `linear-gradient(135deg, ${aura}22 0%, rgba(13,31,58,0.6) 60%)`,
                border: `1px solid ${aura}88`,
              }}
            >
              <div className="text-[0.55rem] uppercase tracking-[0.28em] mb-2" style={{ color: aura }}>
                Featured
              </div>
              <div className="text-3xl font-light leading-tight mb-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                {displayName(featured)}
              </div>
              <p className="text-sm italic leading-relaxed mb-4" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', maxWidth: "48ch" }}>
                {featured.blurb}
              </p>
              <div className="flex items-center gap-3 text-[0.6rem] uppercase tracking-[0.24em]">
                <span className="inline-flex items-center gap-1" style={{ color: TOK.creamMist }}>
                  <Clock size={11} /> {featured.duration_minutes} min
                </span>
              </div>
            </button>

            {/* Rest of the shelf */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="signature-category-rest">
                {rest.map((bp) => (
                  <button
                    key={bp.id}
                    onClick={() => setOpenBp(bp)}
                    data-testid={`signature-card-${bp.id}`}
                    className="text-left rounded-2xl p-4 transition-all"
                    style={{
                      background: `linear-gradient(135deg, ${bp.accent || aura}18 0%, rgba(13,31,58,0.6) 60%)`,
                      border: `1px solid ${bp.accent || aura}55`,
                      color: TOK.cream,
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-lg font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                        {displayName(bp)}
                      </div>
                      {bp.favourite && <Heart size={13} fill={TOK.gold} color={TOK.gold} aria-label="Favourite" data-testid={`signature-card-fav-${bp.id}`} />}
                      {bp.in_library && (
                        <span
                          className="inline-flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.22em] px-2 py-1 rounded-full"
                          style={{ background: "rgba(215,187,122,0.16)", color: TOK.goldSoft }}
                        >
                          <Check size={10} /> Added
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-xs italic leading-relaxed" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
                      {bp.blurb}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-[0.55rem] uppercase tracking-[0.22em]">
                      <span className="inline-flex items-center gap-1" style={{ color: TOK.creamMist }}>
                        <Clock size={10} /> {bp.duration_minutes} min
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {openBp && (
        <DetailSheet
          bp={openBp}
          aura={aura}
          onClose={() => { if (!cloning) { setOpenBp(null); setCloneError(null); } }}
          onPlayNow={(voice) => onPlayNow(openBp, voice)}
          onTweak={() => { onTweak(openBp); setOpenBp(null); setCloneError(null); }}
          cloning={cloning === openBp.id}
          cloneError={cloneError}
          addedId={added[openBp.id]}
          onFav={setFav}
        />
      )}
    </div>
  );
}


/* Shared Play-now / clone flow (used by category + world pages). */
export function useBlueprintPlay() {
  const [cloning, setCloning] = useState(null);
  const [cloneError, setCloneError] = useState(null);
  const [added, setAdded] = useState({});
  const onPlayNow = async (bp, voice) => {
    if (cloning) return;
    setCloneError(null);
    setCloning(bp.id);
    // Retry ONLY on network/timeout/5xx blips. iter266 scar: the
    // clone endpoint can return a deterministic 400 (e.g., missing
    // music asset) — retrying that just wastes an LLM/TTS call and
    // shows the same error a second later. Never retry 4xx.
    const isTransient = (e) => {
      if (!e) return false;
      if (e.code === "ECONNABORTED") return true;      // axios timeout
      if (!e.response) return true;                     // no response == network error
      const s = e.response.status;
      return s >= 500 && s < 600;                       // server error only
    };
    const attempt = async () =>
      axios.post(`${API}/api/audio-journeys/blueprints/${bp.id}/clone${voice ? `?voice=${voice}` : ""}`, {}, {
        ...auth(),
        timeout: 60000,
      });
    try {
      let r;
      try { r = await attempt(); }
      catch (first) {
        if (!isTransient(first)) throw first;
        console.warn("[signature-category] transient clone error, retrying:", first?.message);
        await new Promise((res) => setTimeout(res, 900));
        r = await attempt();
      }
      const jid = r.data?.id;
      // Stay on Signatures: mark it as added; the sheet offers "Open in my library".
      setAdded((m) => ({ ...m, [bp.id]: jid }));
    } catch (e) {
      console.error("[signature-category] clone failed", e);
      const raw = e?.response?.data?.detail || e?.message || "";
      const friendly =
        e?.response?.status === 402
          ? (raw === "signature_limit" ? "You've added your 3 free signature installations. Join Soulumo to add as many as you like." : "This one is part of the Soulumo membership.")
          : e?.response?.status === 400 || e?.response?.status === 404
          ? "This installation is temporarily unavailable — we're fixing it."
          : "The connection dipped. Your recordings are safe — please try again.";
      // Surface inline in the DetailSheet so the user sees it
      // (native alert() is off-brand and gets suppressed in some
      // browsers / headless environments).
      setCloneError(friendly);
      if (raw) console.warn("[signature-category] raw error detail:", raw);
    } finally {
      setCloning(null);
    }
  };

  return { cloning, cloneError, setCloneError, onPlayNow, added };
}

let _cloneReadyCache = null;
function useHasReadyClone() {
  const [ready, setReady] = useState(_cloneReadyCache === true);
  useEffect(() => {
    if (_cloneReadyCache !== null) return;
    axios.get(`${API}/api/voice-clone/me`, auth())
      .then((r) => { _cloneReadyCache = !!(r.data?.enabled && r.data?.status === "ready"); setReady(_cloneReadyCache); })
      .catch(() => { _cloneReadyCache = false; });
  }, []);
  return ready;
}

/* ── Detail sheet ──────────────────────────────────────────────── */
export function DetailSheet({ bp, aura, onClose, onPlayNow, onTweak, cloning, cloneError, addedId, onFav }) {
  const nav = useNavigate();
  const accent = bp.accent || aura || TOK.gold;
  const [listening, setListening] = useState(false);
  const inLibrary = !!addedId || bp.in_library;
  const libraryLink = addedId ? `/my-library?open=${addedId}` : "/my-library";
  // Feb 10 2026 · Preview 8-12 sample affirmations from the blueprint's
  // theme so the DetailSheet gives the listener a felt-sense preview
  // of the words, not just the label + blurb. Cached in-memory once
  // per theme so re-opening is instant.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  useEffect(() => {
    if (!bp?.theme) return;
    let cancelled = false;
    setPreview(null);
    setPreviewErr(null);
    setPreviewLoading(true);
    const flatten = (s) => {
      const flat = [];
      ["truth", "nothing_missing", "trust", "identity", "performance", "final_loop"].forEach((k) => {
        (s[k] || []).slice(0, 2).forEach((t) => t && flat.push(t));
      });
      return flat.slice(0, 12);
    };
    // Instant: fixed/shared script for this blueprint; LLM preview only as fallback.
    axios
      .get(`${API}/api/audio-journeys/blueprints/${bp.id}/sample`, { ...auth(), timeout: 10000 })
      .then((r) => {
        const flat = flatten(r.data?.script || {});
        if (flat.length) return flat;
        return axios
          .post(
            `${API}/api/audio-journeys/preview-script`,
            {
              affirmation_theme: bp.theme,
              duration_minutes: bp.duration_minutes || 10,
              custom_concern: bp.custom_concern || null,
            },
            { ...auth(), timeout: 30000 },
          )
          .then((r2) => flatten(r2.data?.script || {}));
      })
      .then((flat) => {
        if (cancelled) return;
        setPreview(flat);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("[detail-preview] failed:", e?.message);
        setPreviewErr("preview_unavailable");
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [bp?.id, bp?.theme, bp?.duration_minutes, bp?.custom_concern]);
  useEffect(() => {
    const on = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="signature-detail-backdrop"
    >
      <div
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto overscroll-contain"
        style={{
          background: `linear-gradient(180deg, ${accent}12 0%, ${TOK.ink} 60%)`,
          border: `1px solid ${accent}55`,
          color: TOK.cream,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="signature-detail-sheet"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: TOK.goldSoft }}>
              {bp.overnight
                ? `${Math.round((bp.duration_minutes || 0) / 60)} hr · overnight · ${bp.theme}`
                : `${bp.duration_minutes} min · ${bp.theme}`}
            </div>
            <h3
              className="text-3xl font-light leading-tight mt-1"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}
              data-testid="signature-detail-title"
            >
              {displayName(bp)}
            </h3>
            {/* State chips — makes the Hz and delivery mode legible.
                Mar 3 2026 · replaces the "billionaire wave" black-box
                framing with an honest, elegant state map. */}
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="signature-detail-chips">
              <StateChip label={bpFrequencyLabel(bp)} accent={accent} />
              {bp.overnight && <StateChip label="Subliminal · −28 dB" accent={accent} />}
              {bp.overnight && <StateChip label="Seamless loop" accent={accent} />}
            </div>
          </div>
          <FavHeart key={`${bp.id}-${!!bp.favourite}`} bp={bp} onChange={onFav} />
          <button
            onClick={onClose}
            data-testid="signature-detail-close"
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center rounded-full transition-all"
            style={{ width: 44, height: 44, background: "rgba(245,236,217,0.04)", border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}
          >
            <X size={18} />
          </button>
        </div>
        <p className="mt-2 text-base italic leading-relaxed" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }}>
          {bp.blurb}
        </p>
        {bp.custom_concern && (
          <p className="mt-3 text-[0.82rem] leading-relaxed" style={{ color: TOK.creamMist }}>
            {bp.custom_concern}
          </p>
        )}
        {/* Sample affirmations — a felt-sense preview of the words
            the installation will weave. Feb 10 2026 · Michael's
            request to render these horizontally + smaller writing. */}
        <div className="mt-5" data-testid="signature-detail-affirmations">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-[0.55rem] uppercase tracking-[0.28em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              Sample affirmations
            </span>
            <div className="flex-1 h-px" style={{ background: TOK.goldMist }} />
          </div>
          {previewLoading && (
            <div
              className="flex items-center gap-2 text-[0.7rem] italic"
              style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
            >
              <Loader2 size={12} className="animate-spin" /> Gathering the words…
            </div>
          )}
          {previewErr && !previewLoading && (
            <p
              className="text-[0.72rem] italic"
              style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
            >
              You'll hear these when it plays.
            </p>
          )}
          {preview && preview.length > 0 && (
            <ul
              className="grid grid-cols-2 gap-x-3 gap-y-1.5"
              data-testid="signature-detail-affirmations-list"
            >
              {preview.map((line, i) => (
                <li
                  key={`aff-${i}`}
                  className="italic leading-snug"
                  style={{
                    color: TOK.creamDim,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.72rem",
                    letterSpacing: "0.01em",
                  }}
                >
                  · {line}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-6 flex flex-col gap-3">
          {cloneError && (
            <div
              className="rounded-xl px-4 py-3 text-[0.8rem] leading-snug"
              style={{
                background: "rgba(224,122,106,0.12)",
                border: "1px solid rgba(224,122,106,0.4)",
                color: "#f5e0d5",
                fontFamily: '"Cormorant Garamond", serif',
              }}
              role="alert"
              data-testid="signature-detail-error"
            >
              {cloneError}
              {cloneError.startsWith("You've added") && (
                <button type="button" onClick={() => nav("/soulumo/demo?step=pay&reason=signature_limit")} data-testid="signature-detail-join"
                  className="mt-3 block w-full rounded-full py-2.5 text-[0.66rem] uppercase tracking-[0.24em] font-semibold"
                  style={{ background: TOK.gold, color: TOK.ink, fontFamily: '"Manrope", sans-serif' }}>
                  Join Soulumo
                </button>
              )}
            </div>
          )}
          {bp.precomputed_ready && (listening
            ? <SheetPlayer bpId={bp.id} accent={accent} autoPlay />
            : (
              <button
                onClick={() => setListening(true)}
                data-testid="signature-detail-play"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 transition-[opacity,transform] duration-200 active:scale-[0.98]"
                style={{
                  background: TOK.gold, color: TOK.ink,
                  fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
                  letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600, minHeight: 52,
                }}
              >
                <Play size={14} fill="currentColor" /> Play
              </button>
            ))}
          {inLibrary ? (
            <button
              onClick={() => nav(libraryLink)}
              data-testid="signature-detail-open-library"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5"
              style={{
                background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.gold,
                fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
                letterSpacing: "0.28em", textTransform: "uppercase", minHeight: 52,
              }}
            >
              <Check size={14} /> In your library · Open
            </button>
          ) : (
            <button
              onClick={() => onPlayNow(bp.precomputed_ready ? "narrator" : undefined)}
              disabled={cloning}
              data-testid="signature-detail-add-library"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 transition-opacity"
              style={{
                background: bp.precomputed_ready ? "transparent" : TOK.gold,
                border: `1px solid ${TOK.gold}`,
                color: bp.precomputed_ready ? TOK.gold : TOK.ink,
                fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
                letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600,
                minHeight: 52, opacity: cloning ? 0.7 : 1,
              }}
            >
              {cloning ? <Loader2 size={16} className="animate-spin" /> : <Plus size={14} />}
              {cloning ? "Adding…" : "Add to my library"}
            </button>
          )}
          {addedId && !bp.precomputed_ready && (
            <p className="text-center text-[0.78rem] italic" style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif' }} data-testid="signature-detail-preparing">
              We're preparing it now. It will be ready in your library in about a minute.
            </p>
          )}
          {bp.precomputed_ready && !bp.overnight && (
            <button
              onClick={() => nav(`/my-journeys?mine=${bp.id}`)}
              disabled={cloning}
              data-testid="signature-detail-play-my-voice"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 transition-all"
              style={{
                background: "transparent", border: `1px solid ${TOK.gold}`, color: TOK.gold,
                fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
                letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600, minHeight: 52,
              }}
            >
              <Mic size={14} /> Make it in my voice
            </button>
          )}
          <button
            onClick={onTweak}
            disabled={cloning}
            data-testid="signature-detail-tweak"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 transition-all"
            style={{
              background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.cream,
              fontFamily: '"Manrope", sans-serif', fontSize: "0.72rem",
              letterSpacing: "0.28em", textTransform: "uppercase", minHeight: 52,
            }}
          >
            <Wand2 size={14} /> Tweak before creating
          </button>
        </div>
        <div className="mt-5 text-[0.62rem] italic text-center" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
          {bp.overnight
            ? "Play as you fall asleep. Wake as the version of you who lives inside it."
            : bp.precomputed_ready
            ? "Listen here as often as you like. Add it to your library to keep it close."
            : "Add it to your library and we'll prepare it for you, usually in under a minute."}
        </div>
      </div>
    </div>
  );
}

/* ── State chips + helpers · Mar 3 2026 ─────────────────────────
 * Small brand-consistent chip used in the detail sheet to make
 * the Hz and delivery mode legible. Replaces the "billionaire
 * wave" black-box framing with a specific, elegant state map. */
function StateChip({ label, accent }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-[3px] text-[0.55rem] uppercase tracking-[0.22em]"
      style={{
        background: `${accent}18`,
        border: `1px solid ${accent}55`,
        color: "rgba(245,236,217,0.86)",
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "0.22em",
      }}
    >
      {label}
    </span>
  );
}

// Human-readable Hz + state label from the blueprint's binaural
// stages array. Picks the dominant stage (mode). Overnight
// installs are pinned to the theta / 6 Hz story.
function bpFrequencyLabel(bp) {
  if (bp.overnight) return "6 Hz · Theta · Receptive";
  const stages = bp.binaural_stages || [];
  if (!stages.length) return "Alpha · Calm focus";
  const count = {};
  stages.forEach((s) => { count[s] = (count[s] || 0) + 1; });
  const [dom] = Object.entries(count).sort((a, b) => b[1] - a[1])[0];
  const map = {
    delta: "2 Hz · Delta · Deep sleep",
    theta: "6 Hz · Theta · Receptive",
    alpha: "10 Hz · Alpha · Calm focus",
    beta:  "18 Hz · Beta · Active",
    gamma: "40 Hz · Gamma · Peak",
  };
  return map[dom] || `${dom} · State`;
}
